/**
 * The 3D view's measure tool (viewer protocol level 33): a ruler toggle in the
 * top controls strip (its last item, so it never covers the strip on a narrow
 * screen) and, while armed, a bottom-centre readout strip.
 * The viewer owns snapping, placing and drawing; this host owns the button,
 * the polyline and snap flags and the formatting (same readout as the webclient and the
 * Unreal tool). Level 35 adds a Points / Size tab pair: Size pins a clicked
 * twin's oriented bounding box (`measure.mode`, `measureChanged.size`).
 * The editor mounts it only in 3D with a site and a viewer at
 * level >= 33, and remounts it on a viewer reboot, so it starts disarmed.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { fmtArea, fmtDistance } from '@twin-builder/units';
import { useTweaks } from '@/hooks/useTweaks';
import type { MeasureChanged, ViewerPaneHandle } from './ViewerPane';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';

type Props = {
  viewer: RefObject<ViewerPaneHandle>;
  /** The latest `measureChanged`; `armed: false` turns the button off. */
  last: MeasureChanged | null;
  /** Stop placement or a location pick is armed: those take the click, so
   *  measure disarms. */
  otherToolArmed: boolean;
  /** Arming measure: the editor clears stop placement and location picks. */
  onArm: () => void;
  /** The slot in the top controls strip the button renders into (a portal, so
   *  the readout strip below stays positioned against the canvas). */
  buttonHost: HTMLElement | null;
  /** The viewer's `ready.protocolVersion`: the snap chip needs 34, the Size
   *  tab 35. */
  viewerLevel: number;
};

/** The accent as #rrggbb at arm time, or undefined when it is not a hex. */
function accentHex(el: HTMLElement | null): string | undefined {
  if (!el) return undefined;
  const v = getComputedStyle(el).getPropertyValue('--amber').trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  const m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
  return m ? `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toLowerCase() : undefined;
}

export function MeasureTool({ viewer, last, otherToolArmed, onArm, buttonHost, viewerLevel }: Props) {
  const { tweaks, update } = useTweaks();
  const units = tweaks.units;
  const [armed, setArmed] = useState(false);
  const [polyline, setPolyline] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Below level 34 the viewer always snaps (and ignores the flag).
  const snapChip = viewerLevel >= 34;
  const snap = !snapChip || tweaks.measureSnap;
  // Below level 35 the viewer has no Size mode (and ignores the field).
  const sizeTab = viewerLevel >= 35;
  const [mode, setMode] = useState<'points' | 'size'>('points');
  // The report that stood when the tab switched: ignored, so the readout clears.
  const [staleLast, setStaleLast] = useState<MeasureChanged | null>(null);

  const send = (enabled: boolean, poly = polyline, snapOn = snap, md = mode) =>
    viewer.current?.measure({
      enabled,
      polyline: poly,
      snap: snapOn,
      color: enabled ? accentHex(btnRef.current) : undefined,
      ...(sizeTab ? { mode: md } : {}),
    });

  // The viewer turned the tool off (second Escape, or another tool armed).
  useEffect(() => {
    if (last && !last.armed) setArmed(false);
  }, [last]);

  // Stop placement / a location pick took the click: disarm.
  useEffect(() => {
    if (otherToolArmed && armed) {
      send(false);
      setArmed(false);
    }
  }, [otherToolArmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (armed) {
      send(false);
      setArmed(false);
      return;
    }
    onArm();
    if (send(true)) setArmed(true);
  };

  const togglePolyline = () => {
    const next = !polyline;
    setPolyline(next);
    if (armed) send(true, next);
  };

  const toggleSnap = () => {
    const next = !snap;
    update({ measureSnap: next });
    if (armed) send(true, polyline, next);
  };

  const switchMode = (next: 'points' | 'size') => {
    if (next === mode) return;
    setMode(next);
    setStaleLast(last);
    if (armed) send(true, polyline, snap, next);
  };

  // Only the measurement of the current arming and tab (a stale disarmed
  // report, or the one from before a tab switch, says nothing).
  const m = armed && last?.armed && last !== staleLast ? last : null;
  const sizeMode = sizeTab && mode === 'size';
  const size = sizeMode ? m?.size : undefined;
  const segs = !sizeMode ? (m?.segmentsM ?? []) : [];
  const current = segs.length > 0 ? segs[segs.length - 1] : null;
  const rise = m ? fmtDistance(m.riseM, units) : '';

  const hint = sizeMode
    ? size
      ? 'Rest pose. Click another twin, Delete clears, Esc exits'
      : m?.state === 'finished'
        ? 'No 3D asset on this twin'
        : 'Click a twin to measure its size. Esc exits'
    : !m || m.state === 'idle' || m.points.length === 0
      ? snap
        ? 'Click the first point. Snaps to vertices and edges, hold Shift to place freely. Esc exits'
        : 'Click the first point. Places under the cursor, hold Shift to snap once. Esc exits'
      : m.state === 'finished'
        ? 'Click anywhere for a new measurement. Delete clears. Esc exits'
        : polyline
          ? 'Click to add points. Click the last point to finish, the first to close. Delete removes the last'
          : 'Click the second point. Delete removes the first';

  const button = (
      <button
        ref={btnRef}
        className={'oe-measure-btn' + (armed ? ' on' : '')}
        aria-pressed={armed}
        title={armed ? 'Stop measuring' : 'Measure distances, slope and area'}
        onClick={toggle}
      >
        <Icon of={Icons.action.measure} />
      </button>
  );

  return (
    <>
      {buttonHost && createPortal(button, buttonHost)}
      {armed && (
        <div className="oe-measure-strip" role="status" aria-live="polite">
          <div className="oe-measure-row">
            {sizeTab && (
              <div className="oe-measure-tabs" role="group" aria-label="Measure mode">
                <button
                  className={'oe-measure-chip' + (mode === 'points' ? ' on' : '')}
                  aria-pressed={mode === 'points'}
                  title="Measure distances, slope and area between points"
                  onClick={() => switchMode('points')}
                >
                  Points
                </button>
                <button
                  className={'oe-measure-chip' + (mode === 'size' ? ' on' : '')}
                  aria-pressed={mode === 'size'}
                  title="Click a twin to measure its length, width and height"
                  onClick={() => switchMode('size')}
                >
                  Size
                </button>
              </div>
            )}
            {size && (
              <span className="oe-measure-values">
                <span>L <b>{fmtDistance(size.lengthM, units)}</b></span>
                <span>W <b>{fmtDistance(size.widthM, units)}</b></span>
                <span>H <b>{fmtDistance(size.heightM, units)}</b></span>
                <span>Footprint <b>{fmtArea(size.footprintM2, units)}</b></span>
              </span>
            )}
            {current !== null && m && (
              <span className="oe-measure-values">
                <span>Length <b>{fmtDistance(current, units)}</b></span>
                <span>Δh <b>{m.riseM >= 0 ? `+${rise}` : rise}</b></span>
                <span>Slope <b>{m.slopeDeg.toFixed(1)}°</b></span>
                {segs.length > 1 && <span>Total <b>{fmtDistance(m.totalM, units)}</b></span>}
                {m.closed && m.areaM2 != null && <span>Area <b>{fmtArea(m.areaM2, units)}</b></span>}
              </span>
            )}
            {!sizeMode && (
            <button
              className={'oe-measure-chip' + (polyline ? ' on' : '')}
              aria-pressed={polyline}
              title={polyline ? 'Polyline on: add several points' : 'Polyline off: two points measure one distance'}
              onClick={togglePolyline}
            >
              <Icon of={Icons.action.polyline} />
              Polyline
            </button>
            )}
            {snapChip && !sizeMode && (
              <button
                className={'oe-measure-chip' + (snap ? ' on' : '')}
                aria-pressed={snap}
                title={
                  snap
                    ? 'Snapping on. Click to turn off; hold Shift to place freely.'
                    : 'Snapping off. Click to turn on; hold Shift to snap once.'
                }
                onClick={toggleSnap}
              >
                <Icon of={Icons.action.snap} />
                Snap
              </button>
            )}
            <div className="oe-seg" role="group" aria-label="Units">
              <button className={units === 'metric' ? 'on' : ''} onClick={() => update({ units: 'metric' })}>m</button>
              <button className={units === 'imperial' ? 'on' : ''} onClick={() => update({ units: 'imperial' })}>ft</button>
            </div>
          </div>
          <div className="oe-measure-hint">{hint}</div>
        </div>
      )}
    </>
  );
}
